import { useState } from "react";
import { Tabs } from "radix-ui";
import { useBlocker } from "@tanstack/react-router";
import { toast } from "sonner";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Counterstrike } from "@/features/stepper/steptypes/counterstrike";
import {
  editorError,
  useEditor,
  type EditorMission,
  type EditorFields,
} from "./api/use-editor";
import { DoorUpload } from "./door-upload";
import { DoorPreview } from "./door-preview";
import "./mission-editor.css";

function fields(row: EditorMission): EditorFields {
  const { name, mission, helpText, strike, truths, door } = row;
  return { name, mission, helpText: helpText ?? "", strike, truths, door };
}

export function MissionEditor() {
  const [tab, setTab] = useState("mission");
  const [reportSlot, setReportSlot] = useState(1);
  const [selected, setSelected] = useState<EditorMission | null>(null);
  const [reportDrafts, setReportDrafts] = useState<
    Record<number, { text: string; glowId: string | null }>
  >({});
  const api = useEditor(selected?.id);
  const busy =
    api.save.isPending || api.uploadDoor.isPending || api.saveExample.isPending;
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [draft, setDraft] = useState<EditorFields | null>(null);
  const [preview, setPreview] = useState<EditorMission | null>(null);
  const authenticated = api.session.data?.authenticated === true;
  const dirty =
    authenticated &&
    !!selected &&
    !!draft &&
    JSON.stringify(fields(selected)) !== JSON.stringify(draft);
  const reportsDirty = Object.keys(reportDrafts).length > 0;
  const unsaved = dirty || reportsDirty;
  useBlocker({
    shouldBlockFn: () => unsaved && !window.confirm("Discard unsaved changes?"),
    enableBeforeUnload: unsaved,
  });
  const [wasAuthenticated, setWasAuthenticated] = useState(authenticated);
  if (wasAuthenticated !== authenticated) {
    setWasAuthenticated(authenticated);
    if (!authenticated) {
      setReportDrafts({});
      setSelected(null);
      setDraft(null);
      setPreview(null);
    }
  }
  const choose = (row: EditorMission) => {
    if (unsaved && !window.confirm("Discard unsaved changes?")) return;
    setReportDrafts({});
    setSelected(row);
    setDraft(fields(row));
    setPreview(row);
  };
  const act = async (work: () => Promise<unknown>) => {
    try {
      await work();
    } catch (error) {
      toast.error(editorError(error));
    }
  };
  const saveText = async () => {
    if (!selected || !draft) return;
    const updated = await api.save.mutateAsync({
      id: selected.id,
      fields: draft,
    });
    let saved = updated.find((row) => row.id === selected.id);
    if (!saved) {
      const refreshed = await api.missions.refetch();
      saved = refreshed.data?.find((row) => row.id === selected.id);
    }
    setSelected(saved ?? null);
    setDraft(saved ? fields(saved) : null);
    setPreview(saved ?? null);
    toast.success("Mission saved");
    if (!saved)
      toast.error(
        "Text saved, but the mission could not be refreshed. Reload the editor.",
      );
  };
  const rows = api.missions.data ?? [];
  const names = [...new Set(rows.map((row) => row.name))].sort();
  const briefing = selected && draft && (
    <aside>
      <div className="editor-preview-heading">
        <h2>
          {tab === "reports" ? "Field report preview" : "Player briefing"}
        </h2>
        {tab === "mission" && (
          <Button
            variant="outline"
            onClick={() => setPreview({ ...selected, ...draft })}
          >
            Refresh preview
          </Button>
        )}
      </div>
      <p>
        {tab === "reports"
          ? "Your report edits appear here as you type. Save each report to publish its changes."
          : "Uses the player’s briefing component. Refresh to apply your draft."}
      </p>
      <div className="editor-preview">
        {preview && (
          <Counterstrike
            key={`${tab}-${JSON.stringify(preview)}`}
            initialPage={tab === "reports" ? "examples" : "mission"}
            onPageChange={(page) =>
              setTab(page === "examples" ? "reports" : "mission")
            }
            review
            strikeNotice={
              !preview.strike.trim()
                ? "Default text — Enemy’s Strike is empty."
                : undefined
            }
            tier={
              preview.level.toLowerCase() === "improbable"
                ? "improbable"
                : preview.level.toLowerCase() === "challenging"
                  ? "challenging"
                  : "beginner"
            }
            mission={{
              examples: (api.examples.data ?? [])
                .map((report) => ({
                  ...report,
                  text: reportDrafts[report.slot]?.text ?? report.text,
                }))
                .filter(
                  (report) =>
                    report.status !== "unavailable" && report.text.trim(),
                )
                .filter(
                  (report, index, reports) =>
                    reports.findIndex(
                      (candidate) =>
                        (report.glowId !== null &&
                          candidate.glowId === report.glowId) ||
                        candidate.slot === report.slot,
                    ) === index,
                )
                .map((report, index) => ({
                  glowId: report.glowId ?? `draft-${report.slot}`,
                  agent: `Operative ${index + 1}`,
                  did: report.text.trim(),
                  felt: report.description.trim(),
                })),
              missionId: preview.id,
              name: preview.name,
              level: preview.level,
              mission: preview.mission,
              helpText: preview.helpText,
              strike: preview.strike,
              provenTruths: preview.truths.split(","),
              doorNames: [preview.door],
            }}
            acceptLabel="Preview only"
            onAccept={() => {}}
          />
        )}
      </div>
      <p>
        Name and proven truths are not shown on this briefing. The animated
        paper kit uses a separate text file.
      </p>
    </aside>
  );
  return (
    <main className="mission-editor">
      <header>
        <div>
          <p className="editor-eyebrow">SECRET MISSIONS / AUTHORIZED ACCESS</p>
          <h1>Mission Bank editor</h1>
        </div>
        {authenticated && (
          <Button
            variant="outline"
            disabled={api.logout.isPending || busy}
            onClick={() => {
              if (
                !unsaved ||
                window.confirm("Discard unsaved changes and sign out?")
              )
                void act(() => api.logout.mutateAsync());
            }}
          >
            Sign out
          </Button>
        )}
      </header>
      {api.session.isPending ? (
        <p role="status">Checking session…</p>
      ) : api.session.isError ? (
        <div role="alert">
          Unable to check your session.{" "}
          <Button onClick={() => void api.session.refetch()}>Retry</Button>
        </div>
      ) : !authenticated ? (
        <section className="editor-signin">
          <h2>Verify your phone</h2>
          <p>Access is limited to authorized phone numbers.</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void act(async () => {
                await api.sendCode.mutateAsync(phone);
                setSent(true);
                setCode("");
                toast.success("Verification code sent");
              });
            }}
          >
            <label>
              Phone number
              <Input
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  setSent(false);
                }}
                required
              />
            </label>
            <Button disabled={api.sendCode.isPending}>
              {api.sendCode.isPending
                ? "Sending…"
                : sent
                  ? "Resend code"
                  : "Text verification code"}
            </Button>
          </form>
          {sent && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void act(async () => {
                  await api.verify.mutateAsync(code);
                  setCode("");
                  setSent(false);
                });
              }}
            >
              <label>
                Six-digit code
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  required
                />
              </label>
              <Button disabled={api.verify.isPending}>
                {api.verify.isPending ? "Verifying…" : "Enter editor"}
              </Button>
            </form>
          )}
        </section>
      ) : (
        <>
          <section className="editor-selectors">
            <label>
              Mission bank name
              <select
                aria-label="Mission bank name"
                value={selected?.name ?? ""}
                disabled={busy}
                onChange={(event) => {
                  const row = rows.find(
                    (row) => row.name === event.target.value,
                  );
                  if (row) choose(row);
                }}
              >
                <option value="" disabled>
                  Choose a mission
                </option>
                {names.map((name) => (
                  <option key={name}>{name}</option>
                ))}
              </select>
            </label>
            <label>
              Difficulty
              <select
                aria-label="Difficulty"
                value={selected?.id ?? ""}
                disabled={!selected || busy}
                onChange={(event) => {
                  const row = rows.find(
                    (row) => row.id === Number(event.target.value),
                  );
                  if (row) choose(row);
                }}
              >
                <option value="" disabled>
                  Choose a difficulty
                </option>
                {rows
                  .filter((row) => row.name === selected?.name)
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.level || "Unspecified"} · #{row.id}
                    </option>
                  ))}
              </select>
            </label>
          </section>
          {api.missions.isPending && <p role="status">Loading missions…</p>}
          {api.missions.isError && (
            <p role="alert">
              Could not load missions.{" "}
              <Button onClick={() => void api.missions.refetch()}>Retry</Button>
            </p>
          )}
          {api.missions.isSuccess && !rows.length && <p>No missions found.</p>}
          <Tabs.Root value={tab} onValueChange={setTab}>
            <Tabs.List className="editor-tabs" aria-label="Editor sections">
              <Tabs.Trigger value="door">Door</Tabs.Trigger>
              <Tabs.Trigger value="mission">Mission</Tabs.Trigger>
              <Tabs.Trigger value="reports">Field Reports</Tabs.Trigger>
            </Tabs.List>
            {!selected && (
              <p className="editor-empty">
                Choose a mission and difficulty to start editing.
              </p>
            )}
            {selected && draft && (
              <Tabs.Content
                value="mission"
                forceMount
                hidden={tab !== "mission"}
              >
                <div className="editor-columns">
                  <form
                    className="editor-fields"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void act(saveText);
                    }}
                  >
                    <h2>
                      Player-facing text <small>#{selected.id}</small>
                    </h2>
                    <fieldset disabled={busy}>
                      <label>
                        Mission name
                        <Input
                          value={draft.name}
                          maxLength={50}
                          required
                          onChange={(event) =>
                            setDraft({ ...draft, name: event.target.value })
                          }
                        />
                      </label>
                      <label>
                        Mission instructions (large text)
                        <textarea
                          value={draft.mission}
                          rows={6}
                          required
                          onChange={(event) =>
                            setDraft({ ...draft, mission: event.target.value })
                          }
                        />
                      </label>
                      <label>
                        Help text (smaller text)
                        <textarea
                          value={draft.helpText}
                          rows={4}
                          onChange={(event) =>
                            setDraft({ ...draft, helpText: event.target.value })
                          }
                        />
                      </label>
                      <label>
                        Enemy’s strike
                        <textarea
                          value={draft.strike}
                          rows={4}
                          onChange={(event) =>
                            setDraft({ ...draft, strike: event.target.value })
                          }
                        />
                      </label>
                      <label>
                        Proven truths
                        <Input
                          value={draft.truths}
                          maxLength={100}
                          onChange={(event) =>
                            setDraft({ ...draft, truths: event.target.value })
                          }
                        />
                      </label>
                    </fieldset>
                    <div className="editor-actions">
                      <Button type="submit" disabled={!dirty || busy}>
                        {api.save.isPending ? "Saving…" : "Save changes"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!dirty || busy}
                        onClick={() => {
                          if (window.confirm("Discard unsaved changes?")) {
                            setDraft(fields(selected));
                            setPreview(selected);
                          }
                        }}
                      >
                        Discard
                      </Button>
                      <span role="status">
                        {dirty ? "Unsaved changes" : "Saved"}
                      </span>
                    </div>
                  </form>
                  {tab === "mission" && briefing}
                </div>
              </Tabs.Content>
            )}
            {selected && draft && (
              <Tabs.Content
                value="reports"
                forceMount
                hidden={tab !== "reports"}
              >
                <div className="editor-columns">
                  <section className="editor-fields editor-reports">
                    <h2>Field report examples</h2>
                    <p>
                      These are the reports shown in “Show me examples”. Keep
                      the author name and line breaks in the report text. New
                      examples are editor-authored and shared with players.
                    </p>
                    <label className="editor-report-picker">
                      Field report
                      <select
                        aria-label="Field report"
                        value={reportSlot}
                        onChange={(event) =>
                          setReportSlot(Number(event.target.value))
                        }
                      >
                        {[1, 2, 3].map((slot) => (
                          <option key={slot} value={slot}>
                            Example {slot}
                            {reportDrafts[slot] !== undefined
                              ? " · Unsaved"
                              : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    {api.examples.isPending && <p>Loading reports…</p>}
                    {api.examples.isError && (
                      <p role="alert">
                        Unable to load reports.{" "}
                        <Button
                          type="button"
                          onClick={() => void api.examples.refetch()}
                        >
                          Retry reports
                        </Button>
                      </p>
                    )}
                    {api.examples.data?.map((report) => {
                      const text =
                        reportDrafts[report.slot]?.text ?? report.text;
                      const changed = reportDrafts[report.slot] !== undefined;
                      return (
                        <form
                          key={report.slot}
                          hidden={report.slot !== reportSlot}
                          onSubmit={(event) => {
                            event.preventDefault();
                            void act(async () => {
                              await api.saveExample.mutateAsync({
                                id: selected.id,
                                slot: report.slot,
                                glowId: reportDrafts[report.slot]
                                  ? reportDrafts[report.slot].glowId
                                  : report.glowId,
                                text,
                              });
                              setReportDrafts((drafts) => {
                                const next = { ...drafts };
                                delete next[report.slot];
                                return next;
                              });
                              toast.success("Field report saved");
                            });
                          }}
                        >
                          <h3>Example {report.slot}</h3>
                          {report.status === "unavailable" ? (
                            <p role="alert">
                              Linked report #{report.glowId} is unavailable. It
                              may be private, deleted, missing, or linked to
                              another mission.
                            </p>
                          ) : (
                            <>
                              <p>
                                {report.glowId
                                  ? `Report #${report.glowId}`
                                  : "No linked report yet"}
                              </p>
                              <label>
                                Report {report.slot} text
                                <textarea
                                  rows={6}
                                  value={text}
                                  disabled={busy}
                                  required
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setReportDrafts((drafts) => {
                                      const next = { ...drafts };
                                      if (value === report.text)
                                        delete next[report.slot];
                                      else
                                        next[report.slot] = {
                                          text: value,
                                          glowId: drafts[report.slot]
                                            ? drafts[report.slot].glowId
                                            : report.glowId,
                                        };
                                      return next;
                                    });
                                  }}
                                />
                              </label>
                              {report.description && (
                                <p className="editor-report-description">
                                  {report.description}
                                </p>
                              )}
                              <div className="editor-actions">
                                <Button
                                  disabled={busy || !changed || !text.trim()}
                                >
                                  {report.glowId
                                    ? `Save report ${report.slot}`
                                    : `Create example ${report.slot}`}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={busy || !changed}
                                  onClick={() =>
                                    setReportDrafts((drafts) => {
                                      const next = { ...drafts };
                                      delete next[report.slot];
                                      return next;
                                    })
                                  }
                                >
                                  Discard report {report.slot}
                                </Button>
                              </div>
                            </>
                          )}
                        </form>
                      );
                    })}
                  </section>
                  {tab === "reports" && briefing}
                </div>
              </Tabs.Content>
            )}
            {selected && draft && (
              <Tabs.Content value="door" forceMount hidden={tab !== "door"}>
                <div className="editor-columns">
                  <div>
                    <form
                      className="editor-fields"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void act(saveText);
                      }}
                    >
                      <h2>Door</h2>
                      <fieldset disabled={busy}>
                        <label>
                          Door name
                          <Input
                            value={draft.door}
                            maxLength={254}
                            onChange={(event) =>
                              setDraft({ ...draft, door: event.target.value })
                            }
                          />
                        </label>
                      </fieldset>
                      <Button disabled={!dirty || busy}>
                        Save door changes
                      </Button>
                      <p className="editor-hint">
                        Text changes from the Mission tab are saved together.
                      </p>
                    </form>
                    <DoorUpload
                      key={`upload-${selected.id}`}
                      disabled={dirty || busy}
                      pending={api.uploadDoor.isPending}
                      dirty={dirty}
                      onUpload={async (file) => {
                        const result = await api.uploadDoor.mutateAsync({
                          id: selected.id,
                          file,
                        });
                        let saved = result.missions.find(
                          (row) => row.id === selected.id,
                        );
                        if (!saved) {
                          const refreshed = await api.missions.refetch();
                          saved = refreshed.data?.find(
                            (row) => row.id === selected.id,
                          );
                        }
                        setSelected(saved ?? null);
                        setDraft(saved ? fields(saved) : null);
                        setPreview(saved ?? null);
                        toast.success(
                          `Door image saved for ${result.updatedIds.length} mission row(s)`,
                        );
                        if (!saved) {
                          toast.error(
                            "Image saved, but the mission could not be refreshed. Reload the editor.",
                          );
                        }
                      }}
                    />
                  </div>
                  <aside>
                    <div className="editor-preview-heading">
                      <h2>Door preview</h2>
                    </div>
                    <DoorPreview
                      key={`preview-${selected.id}`}
                      image={
                        rows.find(
                          (row) =>
                            !!draft.door &&
                            row.door === draft.door &&
                            row.doorImage,
                        )?.doorImage || selected.doorImage
                      }
                      label={draft.door}
                    />
                  </aside>
                </div>
              </Tabs.Content>
            )}
          </Tabs.Root>
        </>
      )}
    </main>
  );
}
